import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApproveIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import RejectIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';

import { formattedDate } from '@luxuryescapes/lib-refunds/build/lib/formatDate';

import GridPagination from '~/components/Common/Elements/GridPagination';

import BedbankService from '~/services/BedbankService';

interface MappingIssuesResponse {
  result: Array<App.Bedbank.RoomMappingIssue>;
  count: number;
}

const PAGE_SIZE = 15;

const ISSUE_TYPES: Array<App.Bedbank.IssueType> = [
  'ROOM_MAPPING_POST_PURCHASE_FAILED',
  'BOOKED_ROOM_POST_PURCHASE_FAILED',
  'ROOM_MAPPING_INCORRECT_MAPPING',
  'FAILED_RESERVATION',
];

const AI_APPROVAL_ISSUE_TYPES: Array<App.Bedbank.IssueType> = [
  'ROOM_MAPPING_POST_PURCHASE_FAILED',
  'BOOKED_ROOM_POST_PURCHASE_FAILED',
];

function hasAiApproval(issueType: App.Bedbank.IssueType): boolean {
  return AI_APPROVAL_ISSUE_TYPES.includes(issueType);
}

const ACTION_COPY: Record<
  'approve' | 'reject',
  {
    label: 'ACTION' | 'IGNORE';
    verb: 'action' | 'ignore';
    dialogGerund: 'marking as ACTIONED' | 'marking as IGNORED';
    pastTense: 'actioned' | 'ignored';
    pastTenseCapitalized: 'Actioned' | 'Ignored';
  }
> = {
  approve: {
    label: 'ACTION',
    verb: 'action',
    dialogGerund: 'marking as ACTIONED',
    pastTense: 'actioned',
    pastTenseCapitalized: 'Actioned',
  },
  reject: {
    label: 'IGNORE',
    verb: 'ignore',
    dialogGerund: 'marking as IGNORED',
    pastTense: 'ignored',
    pastTenseCapitalized: 'Ignored',
  },
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`issue-tabpanel-${index}`}
      aria-labelledby={`issue-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function RoomMappingIssuesPage() {
  const [mappingIssues, setMappingIssues] = useState<Array<App.Bedbank.RoomMappingIssue>>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [selectedIssueIds, setSelectedIssueIds] = useState<Array<string>>([]);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    action: 'approve' | 'reject' | null;
    count: number;
  }>({
    open: false,
    action: null,
    count: 0,
  });
  const [comment, setComment] = useState<string>('');
  const [activeTab, setActiveTab] = useState<number>(0);
  const [status, setStatus] = useState<'unresolved' | 'resolved'>('unresolved');
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'checkInDate', sort: 'asc' }]);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [aiApprovedFilter, setAiApprovedFilter] = useState(false);
  const activeIssueType = ISSUE_TYPES[activeTab];
  const showAiApproval = hasAiApproval(activeIssueType);

  const filteredIssues = useMemo(() => {
    if (!aiApprovedFilter || !showAiApproval) {
      return mappingIssues;
    }
    return mappingIssues.filter((issue) => 'aiApproved' in issue && issue.aiApproved);
  }, [mappingIssues, aiApprovedFilter, showAiApproval]);

  const { enqueueSnackbar } = useSnackbar();

  const fetchMappingIssues = useCallback(
    async (
      issueType: App.Bedbank.IssueType,
      issueStatus: Array<string> = [],
      page: number,
      pageSize: number,
      bookingNumber?: string,
    ) => {
      setLoading(true);
      try {
        const response: MappingIssuesResponse = await BedbankService.getMappingIssues({
          issueType,
          issueStatus,
          page: page + 1,
          limit: pageSize,
          bookingNumber: bookingNumber?.trim() || undefined,
        });
        setMappingIssues(response.result);
        setTotalCount(response.count || 0);
      } catch (error) {
        enqueueSnackbar('Failed to fetch mapping issues', { variant: 'error' });
        console.error('Error fetching mapping issues:', error);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const getStatusFilter = useCallback((): Array<string> => {
    return status === 'unresolved' ? ['UNACTIONED', 'IN_PROGRESS'] : ['RESOLVED'];
  }, [status]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(bookingSearchQuery);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [bookingSearchQuery]);

  useEffect(() => {
    fetchMappingIssues(
      activeIssueType,
      getStatusFilter(),
      paginationModel.page,
      paginationModel.pageSize,
      debouncedSearchQuery,
    );
  }, [paginationModel, fetchMappingIssues, activeIssueType, getStatusFilter, debouncedSearchQuery]);

  const handlePaginationModelChange = useCallback((newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  }, []);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setAiApprovedFilter(false);
    setSelectedIssueIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleStatusToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setStatus(event.target.checked ? 'resolved' : 'unresolved');
    setSelectedIssueIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setBookingSearchQuery(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setBookingSearchQuery('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  useEffect(() => {
    setSortModel((prev) => {
      if (status === 'unresolved') {
        return [{ field: 'checkInDate', sort: 'asc' }];
      }
      if (prev.length === 0) {
        return prev;
      }
      return [];
    });
  }, [status]);

  const getIssueTypeLabel = useCallback((issueType: App.Bedbank.IssueType) => {
    switch (issueType) {
      case 'ROOM_MAPPING_POST_PURCHASE_FAILED':
        return 'Mapping Failed QA';
      case 'BOOKED_ROOM_POST_PURCHASE_FAILED':
        return 'Booked Room Mismatch QA';
      case 'ROOM_MAPPING_INCORRECT_MAPPING':
        return 'Mapping Deleted';
      case 'FAILED_RESERVATION':
        return 'Failed Reservations';
    }
  }, []);

  const getIssueStatusLabel = useCallback((issueStatus: App.Bedbank.IssueStatus) => {
    switch (issueStatus) {
      case 'UNACTIONED':
        return 'Unresolved';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'RESOLVED':
        return 'Resolved';
    }
  }, []);

  const handleSelectIssue = useCallback((issueId: string, checked: boolean) => {
    setSelectedIssueIds((prev) => (checked ? [...prev, issueId] : prev.filter((id) => id !== issueId)));
  }, []);

  const handleAiApprovedFilterToggle = useCallback(() => {
    setAiApprovedFilter((prev) => !prev);
    setSelectedIssueIds([]);
  }, []);

  const handleBatchApprove = useCallback(() => {
    setConfirmationDialog({
      open: true,
      action: 'approve',
      count: selectedIssueIds.length,
    });
  }, [selectedIssueIds]);

  const handleBatchReject = useCallback(() => {
    setConfirmationDialog({
      open: true,
      action: 'reject',
      count: selectedIssueIds.length,
    });
  }, [selectedIssueIds]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmationDialog.action) {
      return;
    }
    const actionCopy = ACTION_COPY[confirmationDialog.action];
    try {
      const response = await BedbankService.batchResolveMappingIssues({
        issueIds: selectedIssueIds,
        resolution: confirmationDialog.action,
        comments: comment || undefined,
      });
      const { resolved, errors: issueErrors } = response.result;
      if (resolved.length > 0) {
        enqueueSnackbar(
          actionCopy
            ? `${actionCopy.pastTenseCapitalized} ${resolved.length} issue${resolved.length === 1 ? '' : 's'}`
            : `Resolved ${resolved.length} issue${resolved.length === 1 ? '' : 's'}`,
          { variant: 'success' },
        );
      }
      if (issueErrors.length > 0) {
        enqueueSnackbar(`${issueErrors.length} issue${issueErrors.length === 1 ? '' : 's'} failed to resolve`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(`Failed to ${actionCopy.verb} issues`, { variant: 'error' });
    }

    fetchMappingIssues(
      activeIssueType,
      getStatusFilter(),
      paginationModel.page,
      paginationModel.pageSize,
      debouncedSearchQuery,
    );
    setSelectedIssueIds([]);
    setComment('');
    setConfirmationDialog({
      open: false,
      action: null,
      count: 0,
    });
  }, [
    confirmationDialog.action,
    selectedIssueIds,
    comment,
    enqueueSnackbar,
    activeIssueType,
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearchQuery,
    getStatusFilter,
    fetchMappingIssues,
  ]);

  const handleCancelAction = useCallback(() => {
    setComment('');
    setConfirmationDialog({
      open: false,
      action: null,
      count: 0,
    });
  }, []);

  const isSupportedIssueType = useCallback((issueType: App.Bedbank.IssueType) => {
    return (
      issueType === 'ROOM_MAPPING_POST_PURCHASE_FAILED' ||
      issueType === 'BOOKED_ROOM_POST_PURCHASE_FAILED' ||
      issueType === 'ROOM_MAPPING_INCORRECT_MAPPING' ||
      issueType === 'FAILED_RESERVATION'
    );
  }, []);

  const canSelectIssue = useCallback(
    (issue: App.Bedbank.RoomMappingIssue) => {
      const isSupported = isSupportedIssueType(issue.issueType);
      return isSupported;
    },
    [isSupportedIssueType],
  );

  const getDisabledTooltipText = useCallback(
    (issue: App.Bedbank.RoomMappingIssue) => {
      const isSupported = isSupportedIssueType(issue.issueType);

      if (!isSupported) {
        return 'Issue type currently not supported';
      }
      return '';
    },
    [isSupportedIssueType],
  );

  const handleSelectAll = useCallback(() => {
    const selectableIds = filteredIssues.filter(canSelectIssue).map((issue) => issue.id);
    setSelectedIssueIds(selectableIds);
  }, [filteredIssues, canSelectIssue]);

  const handleSelectAllAiApproved = useCallback(() => {
    const aiApprovedIds = filteredIssues
      .filter((issue) => 'aiApproved' in issue && issue.aiApproved && canSelectIssue(issue))
      .map((issue) => issue.id);
    setSelectedIssueIds(aiApprovedIds);
  }, [filteredIssues, canSelectIssue]);

  const handleDeselectAll = useCallback(() => {
    setSelectedIssueIds([]);
  }, []);

  const statusColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueStatus',
    headerName: 'Status',
    flex: 1,
    minWidth: 80,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        {getIssueStatusLabel(params.row.issueStatus)}
      </Typography>
    ),
  };

  const propertyNameColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.propertyName',
    headerName: 'Property Name',
    flex: 1,
    minWidth: 350,
    renderCell: (params) => (
      <Link
        style={{ display: 'flex', alignItems: 'center', height: '100%' }}
        to={`/bedbank/properties/${params.row.issueData.propertyId}`}
      >
        {params.row.issueData.propertyName}
      </Link>
    ),
  };

  const bookedRoomNameColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.roomName',
    headerName: 'Booked Room Name',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', height: '100%' }}
      >
        {'roomName' in params.row.issueData ? params.row.issueData.roomName ?? '' : ''}
      </Typography>
    ),
  };

  const supplierRoomNameColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.supplierRoomName',
    headerName: 'Supplier Room Name',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', height: '100%' }}
      >
        {'supplierRoomName' in params.row.issueData ? params.row.issueData.supplierRoomName ?? '' : ''}
      </Typography>
    ),
  };

  const bookedSupplierRoomNameColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.bookedSupplierRoomName',
    headerName: 'Booked Supplier Room Name',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', height: '100%' }}
      >
        {'bookedSupplierRoomName' in params.row.issueData ? params.row.issueData.bookedSupplierRoomName ?? '' : ''}
      </Typography>
    ),
  };

  const supplierColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.supplierName',
    headerName: 'Supplier',
    flex: 1,
    minWidth: 150,
    renderCell: (params) => (
      <Typography
        variant="body2"
        sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', height: '100%' }}
      >
        {params.row.issueData.supplierName}
      </Typography>
    ),
  };

  const aiApprovedColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'aiApproved',
    headerName: 'AI Approved',
    width: 120,
    sortable: true,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (value, row) => {
      const status = 'aiApprovalStatus' in row ? row.aiApprovalStatus : undefined;
      const approved = 'aiApproved' in row ? row.aiApproved : undefined;
      if (status === 'UNPROCESSED' || status === undefined) return 'pending';
      return approved ? true : false;
    },
    renderCell: (params) => {
      const aiApprovalStatus = 'aiApprovalStatus' in params.row ? params.row.aiApprovalStatus : undefined;
      const aiApproved = 'aiApproved' in params.row ? params.row.aiApproved : undefined;
      const isPending = aiApprovalStatus === 'UNPROCESSED' || aiApprovalStatus === undefined;
      if (isPending) {
        return (
          <Typography
            variant="body2"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}
          >
            -
          </Typography>
        );
      }
      if (aiApproved) {
        return (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <CheckCircleIcon sx={{ color: 'success.main' }} />
          </Box>
        );
      }
      return (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <CancelIcon sx={{ color: 'error.main' }} />
        </Box>
      );
    },
  };

  const getMappingStatusBadgeProps = (
    mappingStatus: App.Bedbank.MappingStatus | undefined,
  ): { label: string; bgcolor: string; color: string } => {
    switch (mappingStatus) {
      case 'DELETED':
        return { label: 'Deleted', bgcolor: 'error.light', color: 'error.dark' };
      case 'CHANGED':
        return { label: 'Changed', bgcolor: 'warning.light', color: 'warning.dark' };
      case 'UNMAPPED':
        return { label: 'Unmapped', bgcolor: 'info.light', color: 'info.dark' };
      default:
        return { label: 'Unknown', bgcolor: 'grey.200', color: 'grey.700' };
    }
  };

  const mappingStatusColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'mappingStatus',
    headerName: 'Mapping Status',
    width: 130,
    sortable: true,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => {
      const mappingStatus = 'mappingStatus' in params.row ? params.row.mappingStatus : undefined;
      const badgeProps = getMappingStatusBadgeProps(mappingStatus);
      return (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Chip
            label={badgeProps.label}
            size="small"
            sx={{
              bgcolor: badgeProps.bgcolor,
              color: badgeProps.color,
              fontWeight: 500,
            }}
          />
        </Box>
      );
    },
  };

  const getBaseColumns = (issueType: App.Bedbank.IssueType): Array<GridColDef<App.Bedbank.RoomMappingIssue>> => {
    switch (issueType) {
      case 'ROOM_MAPPING_POST_PURCHASE_FAILED':
        return [statusColumn, propertyNameColumn, bookedRoomNameColumn, supplierRoomNameColumn, supplierColumn];
      case 'BOOKED_ROOM_POST_PURCHASE_FAILED':
        return [statusColumn, propertyNameColumn, supplierRoomNameColumn, bookedSupplierRoomNameColumn, supplierColumn];
      case 'ROOM_MAPPING_INCORRECT_MAPPING':
        return [
          statusColumn,
          propertyNameColumn,
          bookedRoomNameColumn,
          supplierRoomNameColumn,
          bookedSupplierRoomNameColumn,
          supplierColumn,
          mappingStatusColumn,
        ];
      case 'FAILED_RESERVATION':
        return [statusColumn, propertyNameColumn, supplierColumn];
      default:
        return [
          statusColumn,
          propertyNameColumn,
          bookedRoomNameColumn,
          supplierRoomNameColumn,
          bookedSupplierRoomNameColumn,
          supplierColumn,
        ];
    }
  };

  const baseColumns = getBaseColumns(activeIssueType);

  const roomMappingCheckInDateColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'checkInDate',
    headerName: 'Check-In Date',
    flex: 1,
    minWidth: 150,
    sortable: true,
    valueGetter: (value, row) => {
      const data = row?.issueData;
      const futureBookings =
        data && 'futureBookings' in data ? (data.futureBookings as Array<{ checkinDate: string }>) ?? [] : [];
      if (futureBookings.length === 0) {
        return null;
      }
      const earliestBooking = futureBookings.reduce<string | null>((earliest, booking) => {
        if (!earliest) {
          return booking.checkinDate;
        }
        return booking.checkinDate < earliest ? booking.checkinDate : earliest;
      }, null);
      return earliestBooking || null;
    },
    renderCell: (params) => {
      const data = params?.row?.issueData;
      const futureBookings =
        data && 'futureBookings' in data ? (data.futureBookings as Array<{ checkinDate: string }>) ?? [] : [];

      const earliestBooking = futureBookings.reduce<string | null>((earliest, booking) => {
        if (!earliest) {
          return booking.checkinDate;
        }
        return booking.checkinDate < earliest ? booking.checkinDate : earliest;
      }, null);

      if (!earliestBooking) {
        return (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            -
          </Typography>
        );
      }

      return (
        <Box>
          <Typography variant="body2">{earliestBooking}</Typography>
        </Box>
      );
    },
  };

  const failedReservationCheckInDateColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'checkInDate',
    headerName: 'Check-In Date',
    flex: 1,
    minWidth: 150,
    sortable: true,
    valueGetter: (value, row) => {
      if (row.issueType !== 'FAILED_RESERVATION') {
        return null;
      }
      return row.issueData.checkInDate;
    },
    renderCell: (params) => {
      if (params.row.issueType !== 'FAILED_RESERVATION') {
        return (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            -
          </Typography>
        );
      }

      return (
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.row.issueData.checkInDate}
        </Typography>
      );
    },
  };

  const roomMappingFutureBookingsColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.futureBookings',
    headerName: 'Future Bookings',
    flex: 1,
    minWidth: 200,
    renderCell: (params) => {
      const data = params?.row?.issueData;
      const futureBookings =
        data && 'futureBookings' in data
          ? (data.futureBookings as Array<{ bookingNumber: string; checkinDate: string }>) ?? []
          : [];

      if (futureBookings.length === 0) {
        return (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            -
          </Typography>
        );
      }

      return (
        <Box>
          {futureBookings.map((booking, index) => (
            <Link key={index} to={`/purchases/booking-number/${booking.bookingNumber}`} target="_blank">
              <Typography>
                {booking.bookingNumber} - {booking.checkinDate}
              </Typography>
            </Link>
          ))}
        </Box>
      );
    },
  };

  const failedReservationFutureBookingsColumn: GridColDef<App.Bedbank.RoomMappingIssue> = {
    field: 'issueData.bookingNumber',
    headerName: 'Order',
    flex: 1,
    minWidth: 200,
    renderCell: (params) => {
      if (params.row.issueType !== 'FAILED_RESERVATION') {
        return (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            -
          </Typography>
        );
      }

      const { bookingNumber } = params.row.issueData;
      if (!bookingNumber) {
        return (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            -
          </Typography>
        );
      }

      return (
        <Link to={`/purchases/booking-number/${bookingNumber}`} target="_blank">
          <Typography>{bookingNumber}</Typography>
        </Link>
      );
    },
  };

  const checkInDateColumn =
    activeIssueType === 'FAILED_RESERVATION' ? failedReservationCheckInDateColumn : roomMappingCheckInDateColumn;
  const futureBookingsColumn =
    activeIssueType === 'FAILED_RESERVATION' ? failedReservationFutureBookingsColumn : roomMappingFutureBookingsColumn;

  const columnsUnresolved: Array<GridColDef<App.Bedbank.RoomMappingIssue>> = [
    {
      field: 'select',
      headerName: 'Select',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const canSelect = canSelectIssue(params.row);
        const isSelected = selectedIssueIds.includes(params.row.id);
        const tooltipText = getDisabledTooltipText(params.row);

        const checkbox = (
          <Checkbox
            checked={isSelected}
            disabled={!canSelect}
            onChange={(event) => handleSelectIssue(params.row.id, event.target.checked)}
            sx={{
              opacity: canSelect ? 1 : 0.3,
              '&.Mui-disabled': {
                color: 'text.disabled',
              },
            }}
          />
        );

        return (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight="52px">
            {!canSelect && tooltipText ? (
              <Tooltip title={tooltipText}>
                <span>{checkbox}</span>
              </Tooltip>
            ) : (
              checkbox
            )}
          </Box>
        );
      },
    },
    ...baseColumns,
    ...(showAiApproval ? [aiApprovedColumn] : []),
    checkInDateColumn,
    futureBookingsColumn,
  ];

  const columnsResolved: Array<GridColDef<App.Bedbank.RoomMappingIssue>> = [
    ...baseColumns,
    ...(showAiApproval ? [aiApprovedColumn] : []),
    checkInDateColumn,
    futureBookingsColumn,
    {
      field: 'resolutions',
      headerName: 'Resolution',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        return (
          <Box>
            {params.row.resolutions.map((resolution, index) => (
              <Tooltip
                key={index}
                title={
                  <Box>
                    <Typography variant="body2">
                      <strong>Actioned by:</strong> {resolution.actionedBy}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Comments:</strong> {resolution.comments || 'No comment'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Date:</strong> {formattedDate(new Date(resolution.createdAt))}
                    </Typography>
                  </Box>
                }
                arrow
                componentsProps={{
                  tooltip: {
                    sx: {
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      color: 'text.primary',
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: 2,
                      '& .MuiTooltip-arrow': {
                        color: 'rgba(255, 255, 255, 0.95)',
                        '&::before': {
                          border: '1px solid',
                          borderColor: 'divider',
                        },
                      },
                    },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    cursor: 'help',
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                  }}
                >
                  {resolution.resolution === 'approve'
                    ? ACTION_COPY.approve.pastTenseCapitalized
                    : ACTION_COPY.reject.pastTenseCapitalized}
                </Typography>
              </Tooltip>
            ))}
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="mapping issue types" sx={{ flexGrow: 1 }}>
          {ISSUE_TYPES.map((issueType, index) => (
            <Tab
              key={issueType}
              label={getIssueTypeLabel(issueType)}
              id={`issue-tab-${index}`}
              aria-controls={`issue-tabpanel-${index}`}
            />
          ))}
        </Tabs>
        <FormControlLabel
          sx={{ ml: 2 }}
          control={<Switch color="primary" checked={status === 'resolved'} onChange={handleStatusToggle} />}
          label={status === 'resolved' ? 'Resolved' : 'Unresolved'}
          labelPlacement="start"
        />
      </Box>

      {ISSUE_TYPES.map((issueType, index) => (
        <TabPanel key={issueType} value={activeTab} index={index}>
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 1,
              mb: 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                {status === 'unresolved' ? 'Unresolved' : 'Resolved'} {getIssueTypeLabel(issueType).toLowerCase()}{' '}
                issues
              </Typography>
              {status === 'unresolved' && (
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleBatchApprove}
                    disabled={selectedIssueIds.length === 0}
                    startIcon={<ApproveIcon />}
                  >
                    {ACTION_COPY.approve.label} ({selectedIssueIds.length})
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleBatchReject}
                    disabled={selectedIssueIds.length === 0}
                    startIcon={<RejectIcon />}
                    sx={{
                      bgcolor: 'grey.500',
                      color: 'common.white',
                      '&:hover': {
                        bgcolor: 'grey.600',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'grey.200',
                        color: 'text.disabled',
                      },
                    }}
                  >
                    {ACTION_COPY.reject.label} ({selectedIssueIds.length})
                  </Button>
                </Stack>
              )}
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
              <TextField
                sx={{ width: 360, maxWidth: '100%' }}
                size="small"
                value={bookingSearchQuery}
                onChange={handleSearchChange}
                placeholder="Search by booking number..."
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: bookingSearchQuery ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                          <ClearIcon color="action" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
              {showAiApproval && (
                <FormControlLabel
                  control={
                    <Switch color="primary" checked={aiApprovedFilter} onChange={handleAiApprovedFilterToggle} />
                  }
                  label="AI Approved only"
                  labelPlacement="start"
                />
              )}
              {status === 'unresolved' && (
                <Stack direction="row" spacing={1}>
                  <Button variant="text" size="small" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  {showAiApproval && (
                    <Button variant="text" size="small" onClick={handleSelectAllAiApproved}>
                      Select AI Approved
                    </Button>
                  )}
                  {selectedIssueIds.length > 0 && (
                    <Button variant="text" size="small" onClick={handleDeselectAll}>
                      Deselect All
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
          <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
              columns={status === 'unresolved' ? columnsUnresolved : columnsResolved}
              rows={filteredIssues}
              rowCount={totalCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              loading={loading}
              getRowId={(row) => row.id}
              sortModel={sortModel}
              onSortModelChange={(newModel) => setSortModel(newModel)}
              slots={{
                pagination: GridPagination,
              }}
              pageSizeOptions={[15, 25, 50, 100]}
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              sx={{
                '& .MuiDataGrid-row': {
                  minHeight: '32px !important',
                },
              }}
            />
          </Box>
        </TabPanel>
      ))}

      {/* Confirmation Dialog */}
      <Dialog open={confirmationDialog.open} onClose={handleCancelAction} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirm{' '}
          {confirmationDialog.action ? ACTION_COPY[confirmationDialog.action].dialogGerund : 'marking as ACTIONED'} of{' '}
          {confirmationDialog.count} {confirmationDialog.count === 1 ? 'issue' : 'issues'}?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You are about to {confirmationDialog.action ? ACTION_COPY[confirmationDialog.action].verb : 'action'}{' '}
            {confirmationDialog.count} mapping {confirmationDialog.count === 1 ? 'issue' : 'issues'}. You may add a
            comment to explain your decision (optional).
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Comment (Optional)"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter your comment here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} variant="text">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmationDialog.action === 'approve' ? 'success' : 'primary'}
            sx={
              confirmationDialog.action === 'reject'
                ? {
                    bgcolor: 'grey.500',
                    color: 'common.white',
                    '&:hover': {
                      bgcolor: 'grey.600',
                    },
                  }
                : undefined
            }
          >
            {confirmationDialog.action ? `Confirm ${ACTION_COPY[confirmationDialog.action].label}` : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
